'use client';

import { Menu as MenuPrimitive } from '@base-ui/react/menu';

import { cn } from './utils';

/**
 * Dropdown menu, on @base-ui/react's Menu — same primitive library the other
 * components in this package are built on, so focus management, typeahead,
 * escape/outside-click and the portal all come from the primitive.
 *
 * MenuContent bundles Portal + Positioner + Popup: rendering the popup outside
 * a Positioner leaves it unanchored, which is easy to get wrong at each call
 * site, and every consumer wants the same three layers.
 */
function Menu(props: MenuPrimitive.Root.Props) {
  return <MenuPrimitive.Root {...props} />;
}

function MenuTrigger({ className, ...props }: MenuPrimitive.Trigger.Props) {
  return (
    <MenuPrimitive.Trigger
      className={cn(
        'outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
        className,
      )}
      data-slot="menu-trigger"
      {...props}
    />
  );
}

function MenuContent({
  align = 'end',
  className,
  side = 'bottom',
  sideOffset = 8,
  ...props
}: MenuPrimitive.Popup.Props &
  Pick<MenuPrimitive.Positioner.Props, 'align' | 'side' | 'sideOffset'>) {
  return (
    <MenuPrimitive.Portal>
      {/* Above anything the page stacks, for the reason PopoverContent's
          positioner is. */}
      <MenuPrimitive.Positioner
        align={align}
        className="z-50"
        side={side}
        sideOffset={sideOffset}
      >
        <MenuPrimitive.Popup
          className={cn(
            'min-w-[200px] origin-(--transform-origin) rounded-xl border border-border bg-popover p-1.5 text-sm text-popover-foreground shadow-md outline-none transition-[opacity,transform] duration-150 data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0',
            className,
          )}
          data-slot="menu-content"
          {...props}
        />
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  );
}

function MenuItem({ className, ...props }: MenuPrimitive.Item.Props) {
  return (
    <MenuPrimitive.Item
      className={cn(
        "flex cursor-default items-center gap-2 rounded-lg px-2.5 py-2 text-sm outline-none select-none data-disabled:pointer-events-none data-disabled:opacity-50 data-highlighted:bg-muted [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      data-slot="menu-item"
      {...props}
    />
  );
}

/**
 * Non-interactive header inside a popup (an account line, a section caption).
 *
 * A plain div, not @base-ui's Menu.GroupLabel: that part reads a context only
 * <Menu.Group>/<Menu.RadioGroup> provide and throws without one, and a header
 * that labels nothing is not a group.
 */
function MenuLabel({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('px-2.5 py-2 text-xs', className)}
      data-slot="menu-label"
      {...props}
    />
  );
}

function MenuSeparator({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      aria-hidden="true"
      className={cn('-mx-1.5 my-1 h-px bg-border', className)}
      data-slot="menu-separator"
      {...props}
    />
  );
}

export { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger };
